import React, {useEffect, useState} from "react";

import {AppContext} from "../../HomePage";
import {DashboardService} from "../../service/DashboardService";
import {Loading} from "../../utils/InlineComponents";
import {CampaignContext} from "../../App";
import "./dashboard-ux.css";

// Coverage bar color by level: strong / mid / weak catalog coverage.
const covColor = (rate) => (rate >= 80 ? '#16a34a' : rate >= 50 ? '#d97706' : '#dc2626');

const PAGE_SIZE = 10;

// Merged "Rakip Siteleri" section: joins the URL/health overview
// (getTrackedWebsitesOverview: per-site out-of-stock + invalid, product-based)
// with the per-site catalog-coverage overview
// (getTrackedDistinctWebsitesOverview) into one table keyed by website host.
const OverviewSites = ({isMarket, isManufacturer}) => {

    const {t, setError} = React.useContext(AppContext);
    const {selectedCampaign} = React.useContext(CampaignContext);

    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);

    const dashboardService = new DashboardService();

    useEffect(() => {
        search();
    }, [selectedCampaign]);

    const toMap = (arr) => {
        const m = {};
        (arr || []).forEach(x => { m[x.name] = x.total; });
        return m;
    };

    const search = () => {
        setLoading(true);
        Promise.all([
            dashboardService.getTrackedWebsitesOverview(selectedCampaign.id),
            dashboardService.getTrackedDistinctWebsitesOverview(selectedCampaign.id),
        ]).then(([url, sku]) => {
            const oosMap = toMap(url.out_of_stock_urls);
            const invMap = toMap(url.invalid_urls);
            // SKU list carries per-site product coverage + rate (already sorted by
            // coverage desc); join the product-based health counts onto it by host.
            const merged = (sku.websites || []).map(site => ({
                name: site.name,
                rate: site.rate,
                product: site.total,
                outOfStock: oosMap[site.name] || 0,
                invalid: invMap[site.name] || 0,
            }));
            setRows(merged);
            setPage(0);
            setLoaded(true);
            setLoading(false);
        }).catch(error => {
            setError(error);
            setError(null);
            setLoading(false);
        });
    };

    const siteRow = (r, rank) => (
        <div className="dux-sites-row" key={r.name}>
            <span className="dux-sites-rank">{rank}</span>
            <span className="dux-sites-name">
                <span className="dux-site-host" title={r.name}>{r.name}</span>
            </span>
            <span className="dux-sites-cov">
                <span className="dux-cov-bar"><span className="dux-cov-fill" style={{width: Math.min(100, r.rate) + '%', background: covColor(r.rate)}} /></span>
                <span className="dux-cov-pct">%{r.rate}</span>
            </span>
            <span className="dux-sites-num">{r.product}</span>
            <span className={'dux-sites-num' + (r.outOfStock > 0 ? ' dux-sites-num--amber' : '')}>{r.outOfStock}</span>
            <span className={'dux-sites-num' + (r.invalid > 0 ? ' dux-sites-num--warn' : '')}>{r.invalid}</span>
        </div>
    );

    if (loading) {
        return <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div>;
    }
    if (!loaded) {
        return null;
    }

    // "Rakip" only fits the seller (competitive) profile; for a brand these hosts are
    // tracked reseller/marketplace sites, and for market they are just tracked sites.
    const sectionTitle = isMarket
        ? t('overview.sites', 'Siteler')
        : isManufacturer
            ? t('overview.tracked_sites', 'Takip Edilen Siteler')
            : t('overview.competitor_sites', 'Rakip Siteleri');

    // Brand-protection wording: these hosts sell the brand's products, so the column
    // hints drop the "competes on" framing for the "_brand" variants.
    const tip = (base) => t('overview.' + base + (isManufacturer ? '_brand' : ''), '');

    const totalPages = Math.ceil(rows.length / PAGE_SIZE);
    const start = page * PAGE_SIZE;
    const pageRows = rows.slice(start, start + PAGE_SIZE);

    return (
        <div className="pux-section" style={{marginTop: 8}}>
            <div className="pux-section-title">{sectionTitle}</div>
            <div className="dux-sites">
                <div className="dux-sites-head">
                    <span className="dux-sites-rank">#</span>
                    <span className="dux-sites-name">{t('overview.site', 'Site')}</span>
                    <span className="dux-sites-cov" title={tip('coverage_tip')}>{t('overview.coverage', 'Kapsam')} <i className="pi pi-info-circle dux-tip-i" /></span>
                    <span className="dux-sites-num" title={tip('product_tip')}>{t('overview.product', 'Ürün')} <i className="pi pi-info-circle dux-tip-i" /></span>
                    <span className="dux-sites-num" title={tip('out_of_stock_tip')}>{t('overview.out_of_stock', 'Stok dışı')} <i className="pi pi-info-circle dux-tip-i" /></span>
                    <span className="dux-sites-num" title={tip('invalid_tip')}>{t('overview.invalid', 'Geçersiz')} <i className="pi pi-info-circle dux-tip-i" /></span>
                </div>
                {pageRows.map((r, i) => siteRow(r, start + i + 1))}
            </div>
            {totalPages > 1 &&
                <div className="dux-sites-pager">
                    <button type="button" className="dux-pager-btn" disabled={page === 0}
                            onClick={() => setPage(page - 1)} aria-label={t('actions.previous')}>
                        <i className="pi pi-chevron-left" />
                    </button>
                    <span className="dux-pager-info">{start + 1}–{Math.min(rows.length, start + PAGE_SIZE)} / {rows.length}</span>
                    <button type="button" className="dux-pager-btn" disabled={page >= totalPages - 1}
                            onClick={() => setPage(page + 1)} aria-label={t('actions.next')}>
                        <i className="pi pi-chevron-right" />
                    </button>
                </div>
            }
        </div>
    );
};

export default OverviewSites;
